import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [expandedArticles, setExpandedArticles] = useState({});

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all articles (not just published ones) so users can see their articles immediately
      const response = await fetch('https://api.agromanch.com/api/articles/all');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch articles: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Fetched articles:', data); // Debug log
      setArticles(data);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (imageUrl) => {
    // Validate that we have a proper image URL
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
      setSelectedImage(imageUrl);
      setImageModalOpen(true);
    } else {
      console.warn('Invalid image URL provided to openImageModal:', imageUrl);
    }
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage('');
  };

  const formatDate = (date) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  // Function to toggle article description expansion
  const toggleDescription = (articleId) => {
    setExpandedArticles(prev => ({
      ...prev,
      [articleId]: !prev[articleId]
    }));
  };

  // Function to check if description is long
  const isDescriptionLong = (description) => {
    return description && description.length > 200;
  };

  // Function to render article description with view more/less
  const renderDescription = (article) => {
    const isLong = isDescriptionLong(article.description);
    const isExpanded = expandedArticles[article._id];
    
    if (!isLong) {
      return (
        <Typography 
          sx={{ 
            mb: 2, 
            color: '#333', 
            fontSize: '0.95rem',
            textAlign: 'left',
            wordBreak: 'break-word'
          }}
        >
          {article.description}
        </Typography>
      );
    }
    
    const shouldTruncate = isLong && !isExpanded;
    const displayText = shouldTruncate ? `${article.description.substring(0, 200)}...` : article.description;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography 
          sx={{ 
            color: '#333', 
            fontSize: '0.95rem',
            textAlign: 'left',
            wordBreak: 'break-word'
          }}
        >
          {displayText}
        </Typography>
        <Button 
          onClick={() => toggleDescription(article._id)}
          size="small"
          sx={{ 
            color: 'primary.main', 
            fontWeight: 'bold',
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            mt: 0.5
          }}
        >
          {isExpanded ? 'Show less' : 'View more'}
          <ExpandMoreIcon 
            sx={{ 
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error: {error}
        </Alert>
        <Button onClick={fetchArticles} variant="outlined" sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (articles.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No articles available yet.</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Check back later for new articles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {articles.map((article) => (
        <Card 
          key={article._id} 
          sx={{ 
            mb: 3, 
            borderRadius: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            overflow: 'visible',
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 2 
            }}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#333',
                    lineHeight: 1.2,
                    mb: 1
                  }}
                >
                  {article.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.2
                    }}
                  >
                    By {article.authorName}
                  </Typography>
                  <Chip 
                    icon={<CalendarIcon sx={{ fontSize: '0.75rem !important' }} />} 
                    label={formatDate(article.createdAt)} 
                    size="small" 
                    variant="outlined" 
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                </Box>
              </Box>
            </Box>

            {renderDescription(article)}

            {/* Image display with better error handling and placeholder */}
            {article.image && typeof article.image === 'string' && article.image.trim() !== '' ? (
              <Box 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onClick={() => openImageModal(article.image)}
              >
                <CardMedia 
                  component="img" 
                  image={article.image} 
                  alt={article.title || 'Article image'} 
                  sx={{ 
                    maxHeight: 350, 
                    objectFit: "cover",
                    width: '100%',
                    bgcolor: '#f5f5f5' // Background color while image loads
                  }} 
                  onError={(e) => {
                    // Handle image load errors
                    console.error('Error loading image:', article.image);
                    e.target.style.display = 'none';
                  }}
                />
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 8, 
                  right: 8, 
                  bgcolor: 'rgba(0,0,0,0.6)', 
                  color: 'white', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}>
                  Click to expand
                </Box>
              </Box>
            ) : (
              // Placeholder for articles without images
              <Box 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2">No image available</Typography>
                </Box>
              </Box>
            )}
            
            {article.video && typeof article.video === 'string' && article.video.trim() !== '' && (
              <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                <CardMedia 
                  component="video" 
                  src={article.video} 
                  controls 
                  sx={{ 
                    maxHeight: 350, 
                    objectFit: "cover",
                    width: '100%'
                  }} 
                />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
      
      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={closeImageModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: 0,
            maxHeight: '100vh',
            maxWidth: '100vw'
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <DialogContent sx={{ 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2,
          maxHeight: '100vh',
          maxWidth: '100vw'
        }}>
          {selectedImage && (
            <img 
              src={selectedImage} 
              alt="Expanded view" 
              style={{ 
                maxWidth: 'calc(100vw - 48px)', 
                maxHeight: 'calc(100vh - 48px)', 
                objectFit: 'contain',
                borderRadius: 8
              }} 
              onError={(e) => {
                // Handle image load errors in modal
                console.error('Error loading modal image:', selectedImage);
                e.target.style.display = 'none';
              }}
            />
          )}
          <IconButton
            onClick={closeImageModal}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Articles;